// Stock costs: shipping and duty added to stock value. Draft a cost with
// apportioned freight/duty/insurance components, then post it — posting
// writes one balanced entry (stock value up, what-you-owe or cash down)
// and flips the cost to Posted, which is final.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { AccountPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import {
  type CreateStockCostComponentInput,
  createStockCost,
  listStockCosts,
  STOCK_COST_COMPONENT_KINDS,
  type StockCost,
} from "@/lib/api";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "posted":
      return "success";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function plainKind(kind: string): string {
  const lower = kind.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

interface ComponentDraft {
  uid: string;
  kind: string;
  description: string;
  amount: string;
}

// The whole draft lives on the page, not in the drawer: the drawer unmounts
// when closed and the component editor is long enough that losing it would
// be hostile. Same reasoning as the supplier-credit and recurring pilots,
// minus the session-storage mirror (this draft is not yet worth restoring
// across reloads).
interface StockCostDraft {
  description: string;
  receiptNumber: string;
  inventoryAccountId: string;
  cashAccountId: string;
  costDate: string;
  components: ComponentDraft[];
}

function emptyComponent(): ComponentDraft {
  return {
    uid: crypto.randomUUID(),
    kind: "freight",
    description: "",
    amount: "",
  };
}

function newStockCostDraft(): StockCostDraft {
  return {
    description: "",
    receiptNumber: "",
    inventoryAccountId: "",
    cashAccountId: "",
    costDate: todayInput(),
    components: [emptyComponent()],
  };
}

function draftHasContent(draft: StockCostDraft): boolean {
  return Boolean(
    draft.description ||
      draft.receiptNumber ||
      draft.inventoryAccountId ||
      draft.cashAccountId ||
      draft.components.some(
        (component) => component.description || component.amount,
      ),
  );
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

export function StockCostsPage() {
  const [costs, setCosts] = useState<StockCost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Client-side table controls over the fetched list (the list endpoint has
  // no search/status params — filtering happens here).
  const [statusTab, setStatusTab] = useState("");
  const [search, setSearch] = useState("");

  const [draft, setDraft] = useState<StockCostDraft>(newStockCostDraft);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    listStockCosts()
      .then(setCosts)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const updateDraft = (patch: Partial<StockCostDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const updateComponent = (index: number, patch: Partial<ComponentDraft>) =>
    setDraft((prev) => ({
      ...prev,
      components: prev.components.map((component, i) =>
        i === index ? { ...component, ...patch } : component,
      ),
    }));

  const removeComponent = (index: number) =>
    setDraft((prev) => ({
      ...prev,
      components:
        prev.components.length <= 1
          ? prev.components
          : prev.components.filter((_, i) => i !== index),
    }));

  const addComponent = () =>
    setDraft((prev) => ({
      ...prev,
      components: [...prev.components, emptyComponent()],
    }));

  const clearDraft = () => {
    setDraft(newStockCostDraft());
    setCreateError(null);
  };

  const openDrawer = () => {
    setCreateError(null);
    setDrawerOpen(true);
  };

  const previewMinor = draft.components.reduce(
    (sum, component) => sum + (toMinor(component.amount) ?? 0),
    0,
  );

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setCreateError(null);
    try {
      const trimmed = draft.description.trim();
      if (!trimmed) throw new Error("Description is required.");
      if (!draft.inventoryAccountId)
        throw new Error("Pick the stock account this cost lands in.");
      if (!draft.costDate) throw new Error("Cost date is required.");
      if (draft.components.length === 0) throw new Error("Add at least one cost.");
      const payloadComponents: CreateStockCostComponentInput[] =
        draft.components.map((component, i) => {
          if (!component.description.trim())
            throw new Error(`Cost ${i + 1}: description is required.`);
          const amount_minor = toMinor(component.amount);
          if (amount_minor === null)
            throw new Error(
              `Cost ${i + 1}: amount must be a positive number (e.g. 12.50).`,
            );
          return {
            kind: component.kind,
            description: component.description.trim(),
            amount_minor,
          };
        });
      await createStockCost({
        description: trimmed,
        ...(draft.receiptNumber.trim()
          ? { receipt_number: draft.receiptNumber.trim() }
          : {}),
        inventory_account_id: draft.inventoryAccountId,
        ...(draft.cashAccountId ? { cash_account_id: draft.cashAccountId } : {}),
        cost_date: draft.costDate,
        components: payloadComponents,
      });
      clearDraft();
      setDrawerOpen(false);
      reload();
    } catch (cause) {
      setCreateError(toErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  const statusCounts = (costs ?? []).reduce<Record<string, number>>(
    (counts, cost) => {
      const key = cost.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  const statusTabs = Object.keys(statusCounts).sort();

  const visibleCosts = (costs ?? [])
    .filter((cost) => !statusTab || cost.status.toLowerCase() === statusTab)
    .filter((cost) => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      return (
        cost.stock_cost_number.toLowerCase().includes(query) ||
        cost.description.toLowerCase().includes(query) ||
        (cost.receipt_number ?? "").toLowerCase().includes(query)
      );
    });

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Landed costs"
        description="Shipping and duty added to stock value — draft the freight and duty on a delivery, then post it into stock."
        actions={
          <Button type="button" onClick={openDrawer}>
            New landed cost
          </Button>
        }
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {error && costs === null ? null : (
        <div className="grid gap-3">
          <Tabs
            label="Filter landed costs by status"
            tabs={[
              { value: "", label: `All (${(costs ?? []).length})` },
              ...statusTabs.map((status) => ({
                value: status,
                label: `${plainStatus(status)} (${statusCounts[status]})`,
              })),
            ]}
            value={statusTab}
            onValueChange={setStatusTab}
          >
            <div className="surface grid gap-3 rounded-2xl p-4">
              <div className="flex flex-wrap items-end gap-3">
                <label
                  htmlFor="stock-costs-search"
                  className="grid min-w-40 flex-1 gap-1 text-xs font-medium"
                >
                  Search
                  <Input
                    id="stock-costs-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Number, what arrived or delivery note…"
                    disabled={loading}
                  />
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={reload}
                  disabled={loading}
                >
                  {loading ? "Loading…" : "Refresh"}
                </Button>
              </div>
            </div>

            <div
              className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
              data-testid="stock-costs-table"
            >
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>What arrived</TableHead>
                      <TableHead>Cost date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && costs === null ? (
                      <TableRow>
                        <TableCell colSpan={5}>Loading…</TableCell>
                      </TableRow>
                    ) : (costs ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          No landed costs yet — draft the first one above.
                        </TableCell>
                      </TableRow>
                    ) : visibleCosts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          No landed costs match these filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleCosts.map((cost) => (
                        <TableRow key={cost.id}>
                          <TableCell className="font-mono text-xs">
                            <Link
                              to="/stock-costs/$stockCostId"
                              params={{ stockCostId: cost.id }}
                              className="hover:underline"
                            >
                              {cost.stock_cost_number}
                            </Link>
                          </TableCell>
                          <TableCell>{cost.description}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {new Date(cost.cost_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <StatusDot
                              tone={statusTone(cost.status)}
                              label={plainStatus(cost.status)}
                            />
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: cost.total_minor,
                              currency: cost.currency_code || "USD",
                            })}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div
              className="stack-list grid gap-3 min-[64rem]:hidden"
              data-testid="stock-costs-stack"
            >
              {loading && costs === null ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  Loading…
                </p>
              ) : visibleCosts.length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  {(costs ?? []).length === 0
                    ? "No landed costs yet — draft the first one above."
                    : "No landed costs match these filters."}
                </p>
              ) : (
                visibleCosts.map((cost) => (
                  <article
                    key={cost.id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <Link
                        to="/stock-costs/$stockCostId"
                        params={{ stockCostId: cost.id }}
                        className="record-title underline-offset-4 hover:underline"
                      >
                        {cost.stock_cost_number}
                      </Link>
                      <span className="tabular-nums">
                        {formatMoney({
                          cents: cost.total_minor,
                          currency: cost.currency_code || "USD",
                        })}
                      </span>
                    </div>
                    <CardField label="What arrived">{cost.description}</CardField>
                    {cost.receipt_number ? (
                      <CardField label="Delivery note">
                        {cost.receipt_number}
                      </CardField>
                    ) : null}
                    <CardField label="Cost date">
                      {new Date(cost.cost_date).toLocaleDateString()}
                    </CardField>
                    <CardField label="Status">
                      <StatusDot
                        tone={statusTone(cost.status)}
                        label={plainStatus(cost.status)}
                      />
                    </CardField>
                    <div className="flex flex-wrap gap-2 pt-3">
                      <Button asChild variant="secondary" size="sm">
                        <Link
                          to="/stock-costs/$stockCostId"
                          params={{ stockCostId: cost.id }}
                        >
                          Open landed cost
                        </Link>
                      </Button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </Tabs>
        </div>
      )}

      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title="New landed cost"
        description="Draft only — nothing posts until the cost is posted from its detail page. Components are your per-delivery split; the number is assigned for you. Closing this panel keeps your draft on this page."
        className="w-[min(46rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDrawerOpen(false)}
            >
              Back to landed costs
            </Button>
            {draftHasContent(draft) ? (
              <Button type="button" variant="ghost" onClick={clearDraft}>
                Discard draft
              </Button>
            ) : null}
            <Button
              type="submit"
              form="stock-cost-create-form"
              disabled={submitting}
              className="ms-auto"
            >
              {submitting ? "Drafting…" : "Draft landed cost"}
            </Button>
          </>
        }
      >
        <form
          id="stock-cost-create-form"
          onSubmit={submit}
          className="grid gap-3"
          aria-label="Create landed cost"
        >
          {createError ? (
            <div
              role="alert"
              className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
            >
              {createError}
            </div>
          ) : null}
          <label
            htmlFor="lc-description"
            className="grid gap-1 text-xs font-medium"
          >
            What arrived
            <Input
              id="lc-description"
              value={draft.description}
              onChange={(e) => updateDraft({ description: e.target.value })}
              required
              placeholder="September fabric delivery — freight + duty"
              disabled={submitting}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label
              htmlFor="lc-receipt-number"
              className="grid gap-1 text-xs font-medium"
            >
              Delivery note (optional)
              <Input
                id="lc-receipt-number"
                value={draft.receiptNumber}
                onChange={(e) =>
                  updateDraft({ receiptNumber: e.target.value })
                }
                placeholder="GRN-2041"
                disabled={submitting}
              />
            </label>
            <label htmlFor="lc-date" className="grid gap-1 text-xs font-medium">
              Cost date
              <Input
                id="lc-date"
                type="date"
                value={draft.costDate}
                onChange={(e) => updateDraft({ costDate: e.target.value })}
                required
                disabled={submitting}
              />
            </label>
          </div>
          <label
            htmlFor="lc-inventory-account"
            className="grid gap-1 text-xs font-medium"
          >
            Stock account
            <AccountPicker
              id="lc-inventory-account"
              value={draft.inventoryAccountId}
              onChange={(inventoryAccountId) =>
                updateDraft({ inventoryAccountId })
              }
              disabled={submitting}
              required
            />
          </label>
          <div className="grid gap-1 text-xs font-medium">
            <label htmlFor="lc-cash-account">
              Paid from (leave blank when unpaid)
            </label>
            <AccountPicker
              id="lc-cash-account"
              value={draft.cashAccountId}
              onChange={(cashAccountId) => updateDraft({ cashAccountId })}
              disabled={submitting}
            />
            <p className="text-xs font-normal text-[var(--muted-foreground)]">
              Blank means unpaid — the cost sits in what you owe until it is
              paid. Picking an account means it is paid from there now.
            </p>
          </div>
          <fieldset className="grid gap-2">
            <legend className="text-xs font-medium">Extra costs</legend>
            {draft.components.map((component, index) => (
              <div key={component.uid} className="grid gap-2">
                <div className="grid gap-2 sm:grid-cols-[1fr_2fr] sm:items-end">
                  <label
                    htmlFor={`lc-component-${index}-kind`}
                    className="grid gap-1 text-xs font-medium"
                  >
                    Kind
                    <select
                      id={`lc-component-${index}-kind`}
                      className="select"
                      value={component.kind}
                      onChange={(e) =>
                        updateComponent(index, { kind: e.target.value })
                      }
                      disabled={submitting}
                      title="What kind of extra cost this is"
                    >
                      {STOCK_COST_COMPONENT_KINDS.map((kind) => (
                        <option key={kind} value={kind}>
                          {plainKind(kind)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label
                    htmlFor={`lc-component-${index}-description`}
                    className="grid gap-1 text-xs font-medium"
                  >
                    What is this for
                    <Input
                      id={`lc-component-${index}-description`}
                      value={component.description}
                      onChange={(e) =>
                        updateComponent(index, { description: e.target.value })
                      }
                      required
                      placeholder="Ocean freight — container 42"
                      disabled={submitting}
                    />
                  </label>
                </div>
                <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
                  <label
                    htmlFor={`lc-component-${index}-amount`}
                    className="grid gap-1 text-xs font-medium"
                  >
                    Amount (USD)
                    <Input
                      id={`lc-component-${index}-amount`}
                      type="number"
                      value={component.amount}
                      onChange={(e) =>
                        updateComponent(index, { amount: e.target.value })
                      }
                      required
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      disabled={submitting}
                    />
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeComponent(index)}
                    disabled={submitting || draft.components.length <= 1}
                    aria-label={`Remove cost ${index + 1}`}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={addComponent}
                disabled={submitting}
              >
                Add cost
              </Button>
            </div>
          </fieldset>
          <p
            className="text-sm text-[var(--muted-foreground)]"
            aria-live="polite"
          >
            Total into stock:{" "}
            <span className="font-semibold text-[var(--foreground)]">
              {formatMoney({ cents: previewMinor, currency: "USD" })}
            </span>
          </p>
        </form>
      </Drawer>
    </div>
  );
}
